import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { apiErrorCode, apiErrorMessage } from '@/api/client'
import { rewards as api } from '@/api/endpoints'
import { HelpTip, Input, Select, Spinner } from '@/components/ui'
import { cn } from '@/lib/utils'
import type { RewardType } from '@/types/api'

interface Props {
  bonuses: RewardType[]
  currency: string
  onMessage: (message: { kind: 'success' | 'error'; text: string } | null) => void
}

/**
 * Vlastné bonusy inštruktorov (`sql/038`) - napr. „Request“ = 1 €, keď si
 * klient vyžiadal konkrétneho inštruktora. Platia pre všetkých inštruktorov;
 * v karte klientskej rezervácie sa ukážu ako checkboxy a zaškrtnutý bonus
 * sa pripočíta inštruktorovi lekcie.
 */
export default function RewardBonuses({ bonuses, currency, onMessage }: Props) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [draft, setDraft] = useState<Record<number, { name: string; reward: string }>>({})
  const [fresh, setFresh] = useState({ name: '', reward: '', per: 'lesson' as RewardType['per'] })

  useEffect(() => {
    setDraft(Object.fromEntries(bonuses.map((b) => [b.id, { name: b.name ?? '', reward: String(b.reward ?? 0) }])))
  }, [bonuses])

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['reward-types'] })
    // Checkboxy v rezervácii idú z `bootstrap` (recepcia typy odmien nečíta).
    void qc.invalidateQueries({ queryKey: ['bootstrap'] })
  }
  const onError = (err: unknown) => onMessage({ kind: 'error', text: apiErrorMessage(err) })

  const save = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Partial<RewardType> }) => api.saveType(id, payload),
    onSuccess: () => {
      onMessage({ kind: 'success', text: t('global.saved') })
      refresh()
    },
    onError,
  })
  const create = useMutation({
    mutationFn: () =>
      api.createBonus({ name: fresh.name.trim(), reward: String(Number.parseFloat(fresh.reward)), per: fresh.per }),
    onSuccess: () => {
      setFresh({ name: '', reward: '', per: 'lesson' })
      onMessage({ kind: 'success', text: t('global.saved') })
      refresh()
    },
    onError,
  })
  const remove = useMutation({
    mutationFn: (id: number) => api.deleteType(id),
    onSuccess: () => {
      onMessage({ kind: 'success', text: t('settings.bonusDeleted') })
      refresh()
    },
    // Používaný bonus sa nemaže, len vypína - odmeny by stratili svoj typ.
    onError: (err) =>
      apiErrorCode(err) === 'rewardType.inUse'
        ? onMessage({ kind: 'error', text: t('settings.bonusInUse') })
        : onError(err),
  })

  const freshAmount = Number.parseFloat(fresh.reward)
  const canAdd = fresh.name.trim() !== '' && !Number.isNaN(freshAmount) && freshAmount >= 0

  return (
    <section className="card space-y-2 p-3">
      <h2 className="flex items-center gap-1.5 text-sm font-semibold">
        {t('settings.bonuses')}
        <HelpTip label={t('settings.bonuses')}>{t('settings.bonusesHint')}</HelpTip>
      </h2>
      <table className="w-full">
        <tbody>
          {bonuses.map((bonus) => {
            const row = draft[bonus.id] ?? { name: bonus.name ?? '', reward: String(bonus.reward) }
            return (
              <tr
                key={bonus.id}
                className={cn('border-b border-white/70 last:border-b-0', bonus.allowed ? 'bg-green-50' : 'bg-slate-50')}
              >
                <td className="px-2 py-1.5">
                  <Input
                    value={row.name}
                    maxLength={100}
                    aria-label={t('settings.bonusName')}
                    onChange={(e) => setDraft({ ...draft, [bonus.id]: { ...row, name: e.target.value } })}
                    onBlur={() => {
                      const name = row.name.trim()
                      if (!name) {
                        setDraft({ ...draft, [bonus.id]: { ...row, name: bonus.name ?? '' } })
                        return
                      }
                      if (name !== bonus.name) save.mutate({ id: bonus.id, payload: { name } })
                    }}
                  />
                </td>
                <td className="w-36 whitespace-nowrap px-2 py-1.5">
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      className="w-20 text-right"
                      aria-label={t('settings.bonusAmount')}
                      value={row.reward}
                      onChange={(e) => setDraft({ ...draft, [bonus.id]: { ...row, reward: e.target.value } })}
                      onBlur={() => {
                        const next = Number.parseFloat(row.reward)
                        if (Number.isNaN(next) || next < 0) {
                          setDraft({ ...draft, [bonus.id]: { ...row, reward: String(bonus.reward) } })
                          return
                        }
                        if (next !== Number(bonus.reward)) save.mutate({ id: bonus.id, payload: { reward: String(next) } })
                      }}
                    />
                    <span className="text-[13px] font-medium text-slate-600">{currency}</span>
                  </div>
                </td>
                <td className="w-40 px-2 py-1.5">
                  <Select
                    value={bonus.per}
                    aria-label={t('settings.bonusPer')}
                    onChange={(e) => save.mutate({ id: bonus.id, payload: { per: e.target.value as RewardType['per'] } })}
                  >
                    <option value="lesson">{t('settings.bonusPerLesson')}</option>
                    <option value="hour">{t('settings.bonusPerHour')}</option>
                  </Select>
                </td>
                <td className="w-32 px-2 py-1.5">
                  <button
                    type="button"
                    onClick={() => save.mutate({ id: bonus.id, payload: { allowed: !bonus.allowed } })}
                    disabled={save.isPending}
                    className={cn(
                      'rounded px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-white',
                      bonus.allowed ? 'bg-green-600 hover:bg-green-700' : 'bg-slate-400 hover:bg-slate-500',
                    )}
                  >
                    {bonus.allowed ? t('settings.rewardAllowed') : t('settings.rewardDisabled')}
                  </button>
                </td>
                <td className="w-10 px-2 py-1.5 text-right">
                  <button
                    type="button"
                    className="text-slate-400 hover:text-red-600"
                    title={t('global.delete')}
                    onClick={() => {
                      if (window.confirm(t('settings.bonusDeleteConfirm', { name: bonus.name }))) remove.mutate(bonus.id)
                    }}
                  >
                    <Trash2 size={15} />
                  </button>
                </td>
              </tr>
            )
          })}
          <tr>
            <td className="px-2 py-1.5">
              <Input
                value={fresh.name}
                maxLength={100}
                placeholder={t('settings.bonusNamePlaceholder')}
                onChange={(e) => setFresh({ ...fresh, name: e.target.value })}
              />
            </td>
            <td className="px-2 py-1.5">
              <div className="flex items-center gap-1">
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  className="w-20 text-right"
                  placeholder="0"
                  value={fresh.reward}
                  onChange={(e) => setFresh({ ...fresh, reward: e.target.value })}
                />
                <span className="text-[13px] font-medium text-slate-600">{currency}</span>
              </div>
            </td>
            <td className="px-2 py-1.5">
              <Select value={fresh.per} onChange={(e) => setFresh({ ...fresh, per: e.target.value as RewardType['per'] })}>
                <option value="lesson">{t('settings.bonusPerLesson')}</option>
                <option value="hour">{t('settings.bonusPerHour')}</option>
              </Select>
            </td>
            <td className="px-2 py-1.5" colSpan={2}>
              <button
                type="button"
                className="btn-secondary px-2 py-1 text-xs"
                disabled={!canAdd || create.isPending}
                onClick={() => {
                  onMessage(null)
                  create.mutate()
                }}
              >
                {create.isPending ? <Spinner /> : <Plus size={13} />}
                {t('settings.bonusAdd')}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  )
}
